import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from 'reactstrap';
import useSubmittedBidder from '../../../hooks/useSubmittedBidder';
import useClarificationBidders from '../../../hooks/useClarificationBidders';
import TenderNotClosedAlert from '../EvaluationOperational/Components/TenderNotClosedAlert';
import {
  displayValue,
  canAccessEvaluationOperationalData,
  getEvaluationOperationalLockReason,
  isEvaluationSaved,
} from '../../../utils/tenderEvaluationHelpers';
import {
  CLARIFICATION_STATUS,
  getClarificationStatusBadge,
} from '../../../../../shared/offer-domain/clarification';
import {
  hasProposalValidityCounter,
  withBidderCounter,
} from '../../../../../shared/offer-domain/counter';
import { DateCountdown } from '../../../../../shared/offer-domain/ui';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { EmptyState } from '../../../../../Components/Common/ListStates';
import { SkeletonTable } from '../../../../../Components/Common/Skeleton';
import StatusBadge, {
  colorToTone,
} from '../../../../../Components/Common/StatusBadge';
import notify from '../../../../../utils/notify';
import {
  downloadClarificationDocx,
  printClarificationPdf,
} from '../../../utils/clarificationDocument';

const getValidityCountdownProps = (
  procurement,
  bidder,
  { compact = false } = {}
) => {
  const merged = withBidderCounter(procurement, bidder) || procurement || {};
  const hasCounter = hasProposalValidityCounter(
    merged?.proposalValidityPeriodMaxCounter
  );
  if (compact) {
    return {
      deadline: hasCounter
        ? merged.proposalValidityPeriodMaxCounter
        : merged?.proposalValidityPeriodMax,
      label: hasCounter ? 'Validity' : 'Procurement',
      openLabel: hasCounter ? 'Ends in' : 'Procurement ends in',
      closedLabel: hasCounter ? 'Ended' : 'Procurement ended',
    };
  }
  return {
    deadline: hasCounter
      ? merged.proposalValidityPeriodMaxCounter
      : merged?.proposalValidityPeriodMax,
    label: hasCounter ? 'Validity date' : 'Validity Procurement',
    openLabel: hasCounter ? 'Validity ends in' : 'Validity Procurement ends in',
    closedLabel: hasCounter ? 'Validity ended' : 'Validity Procurement ended',
  };
};

const Clarification = ({
  procurementId,
  procurement,
  isLoadingProcurement = false,
  unreadByMitraId = {},
  isActive = true,
  refreshToken = 0,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const canAccessData = canAccessEvaluationOperationalData(procurement);
  const lockReason = getEvaluationOperationalLockReason(procurement);
  const canDownloadReport = isEvaluationSaved(procurement);
  const { items, isLoading } = useClarificationBidders(procurementId, {
    enabled: Boolean(procurementId && canAccessData),
    isActive,
    refreshToken,
  });
  // Full offer/clause data for the PDF only; table rows come from clarification-bidders.
  const { items: submittedBidders } = useSubmittedBidder(procurementId, {
    enabled: Boolean(procurementId && canAccessData),
    isActive,
    refreshToken,
  });

  if (isLoadingProcurement && !procurement) {
    return <SkeletonTable rows={4} columns={4} />;
  }

  if (!canAccessData) {
    return (
      <TenderNotClosedAlert featureLabel='Clarification' reason={lockReason} />
    );
  }

  const handleViewDetail = bidder => {
    if (!procurementId || !bidder.id) return;
    navigate(
      `/apps-tenderevaluation/dtl/${procurementId}/clarification/${encodeURIComponent(bidder.id)}`
    );
  };

  const handleDownloadDocument = async (row, format = 'pdf') => {
    const full = submittedBidders.find(b => String(b.id) === String(row.id));
    const params = {
      procurement,
      bidder: {
        ...full,
        id: full?.id ?? row.id,
        procurementMitraId: full?.id ?? row.id,
        companyName: row.companyName,
      },
    };
    try {
      if (format === 'docx') await downloadClarificationDocx(params);
      else await printClarificationPdf(params);
    } catch {
      notify.error('Failed to generate report. Please try again.');
    }
  };

  const listHeader = (
    <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3'>
      <h5 className='mb-0 fw-semibold'>List of Offering</h5>
      <DateCountdown
        label='Validity Procurement'
        deadline={procurement?.proposalValidityPeriodMax}
        openLabel='Validity Procurement ends in'
        closedLabel='Validity Procurement ended'
      />
    </div>
  );

  if (isLoading) {
    return (
      <>
        {listHeader}
        <SkeletonTable rows={4} columns={4} />
      </>
    );
  }

  if (!items.length) {
    return (
      <>
        {listHeader}
        <EmptyState
          compact
          icon='ri-chat-quote-line'
          title='No offers to clarify yet'
          emptyHint='Clarification opens once a bidder has submitted an offer for this tender.'
        />
      </>
    );
  }

  return (
    <>
      {listHeader}
      {readOnly ? (
        <p className='text-muted small mb-3'>
          Evaluation is locked. Clarification is view-only.
        </p>
      ) : null}
      <div className='table-responsive'>
        <Table className='align-middle mb-0' aria-label='List of offering'>
          <thead className='table-light'>
            <tr>
              <th className='fs-13 text-nowrap'>Mitra</th>
              <th className='fs-13 text-nowrap'>Status Clarification</th>
              <th className='fs-13 text-center text-nowrap'>GTC Amend</th>
              <th className='fs-13' style={{ width: 128, maxWidth: 132 }}>
                Validity
              </th>
              <th className='fs-13 text-center text-nowrap'>Download</th>
              <th className='fs-13 text-center text-nowrap'>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(bidder => {
              const statusBadge = getClarificationStatusBadge(
                bidder.statusClarification,
                bidder.statusClarificationLabel
              );
              const isWaitingPanitia =
                Number(bidder.statusClarification) ===
                  CLARIFICATION_STATUS.WAITING_PANITIA ||
                String(statusBadge.label).toLowerCase() ===
                  'waiting response from panitia tender';
              const hasUnread = Boolean(unreadByMitraId[String(bidder.id)]);
              const fullBidder =
                submittedBidders.find(
                  b => String(b.id) === String(bidder.id)
                ) || bidder;
              const gtcAmendCount = fullBidder?.gtcAmendedClauseCount ?? 0;
              const validityCountdown = getValidityCountdownProps(
                procurement,
                fullBidder,
                { compact: true }
              );
              return (
                <tr
                  key={bidder.id}
                  className={
                    isWaitingPanitia
                      ? 'table-warning'
                      : hasUnread
                        ? 'table-info'
                        : undefined
                  }
                >
                  <td
                    className={
                      hasUnread && !isWaitingPanitia
                        ? 'text-info fs-13 fw-semibold'
                        : 'text-dark fs-13'
                    }
                  >
                    {displayValue(bidder.companyName)}
                  </td>
                  <td className='text-dark fs-13'>
                    <StatusBadge
                      tone={colorToTone(statusBadge.color)}
                      label={statusBadge.label}
                      size='sm'
                    />
                  </td>
                  <td className='text-center'>
                    <StatusBadge
                      tone={gtcAmendCount > 0 ? 'primary' : 'muted'}
                      label={`GTC Amend: ${gtcAmendCount}`}
                      size='sm'
                    />
                  </td>
                  <td
                    className='text-dark fs-13'
                    style={{ width: 128, maxWidth: 132 }}
                  >
                    <DateCountdown {...validityCountdown} compact />
                  </td>
                  <td className='text-center'>
                    <div className='d-inline-flex gap-1'>
                      <ActionButton
                        variant='secondary'
                        size='sm'
                        icon='ri-download-2-line'
                        disabled={!canDownloadReport}
                        onClick={() => handleDownloadDocument(bidder, 'pdf')}
                      >
                        Download
                      </ActionButton>
                      <ActionButton
                        variant='secondary'
                        size='sm'
                        icon='ri-file-word-2-line'
                        disabled={!canDownloadReport}
                        onClick={() => handleDownloadDocument(bidder, 'docx')}
                      >
                        Download Word
                      </ActionButton>
                    </div>
                  </td>
                  <td className='text-center'>
                    <ActionButton
                      variant='icon'
                      size='sm'
                      icon='ri-eye-line'
                      label='Detail'
                      onClick={() => handleViewDetail(bidder)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default Clarification;
